// useImportacionesListXL - Sprint 44 Fase 3 Vista 10.
// Hook lista importaciones con filtros + pager + msg auto-dismiss.
// Escucha CustomEvent('couriers:changed') para refrescar al volver del editor.
import { useCallback, useEffect, useRef, useState } from 'react';
import apiClient from '../../../api/apiClient';

const DEFAULT_PER_PAGE = 25;
const MSG_AUTO_DISMISS_MS = 4000;

export default function useImportacionesListXL() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);

  const [page, setPage] = useState(1);
  const [perPage] = useState(DEFAULT_PER_PAGE);
  const [tipo, setTipo] = useState('');
  const [q, setQ] = useState('');
  const [msg, setMsg] = useState(null);

  const msgTimer = useRef(null);
  const pushMsg = useCallback((kind, text) => {
    if (msgTimer.current) clearTimeout(msgTimer.current);
    setMsg({ kind, text });
    msgTimer.current = setTimeout(() => setMsg(null), MSG_AUTO_DISMISS_MS);
  }, []);
  useEffect(() => () => { if (msgTimer.current) clearTimeout(msgTimer.current); }, []);

  const load = useCallback(async () => {
    setLoading(true); setErr(null);
    try {
      const params = new URLSearchParams();
      params.set('page', String(page));
      params.set('per_page', String(perPage));
      if (tipo) params.set('tipo', tipo);
      if (q) params.set('q', q);
      const r = await apiClient.get('/importaciones/?' + params.toString());
      const d = (r && r.data) || {};
      setItems(d.items || []);
      setTotal(d.total || 0);
    } catch (e) {
      setErr((e && e.message) || 'Error al cargar');
      setItems([]); setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [page, perPage, tipo, q]);

  useEffect(() => { load(); }, [load]);

  // Refrescar al volver del editor de couriers o al re-enfocar la ventana.
  useEffect(() => {
    const refresh = () => load();
    window.addEventListener('couriers:changed', refresh);
    window.addEventListener('focus', refresh);
    return () => {
      window.removeEventListener('couriers:changed', refresh);
      window.removeEventListener('focus', refresh);
    };
  }, [load]);

  const resetAndSetTipo = useCallback((v) => { setPage(1); setTipo(v); }, []);
  const resetAndSetQ    = useCallback((v) => { setPage(1); setQ(v); }, []);

  const totalPages = Math.max(1, Math.ceil((total || 0) / (perPage || 1)));
  const canPrev = page > 1;
  const canNext = page < totalPages;

  const remove = useCallback(async (id) => {
    try {
      await apiClient.delete('/importaciones/' + id);
      pushMsg('ok', 'Importacion eliminada');
      await load();
    } catch (e) {
      pushMsg('err', (e && e.message) || 'Error al eliminar');
    }
  }, [load, pushMsg]);

  return {
    items, total, loading, err,
    page, perPage, setPage,
    totalPages, canPrev, canNext,
    tipo, setTipo: resetAndSetTipo,
    q, setQ: resetAndSetQ,
    msg, pushMsg, clearMsg: () => setMsg(null),
    load, remove,
  };
}
